import { BackendItem, BackendSubItem } from '../features/items/types/backend-item.types';
import { calculateWeights, parseMaterialGradeId } from '../shared/utils/calculations.utils';
import { formatDate } from '../shared/utils/formatters.utils';

import { Item } from '../features/items/types/item.types';
import { SubItem } from '../features/items/types/sub-item.types';
import omitBy from 'lodash/fp/omitBy';
import { pduPruReadableNames } from '../features/items/types/pdu-pru.constants';

const DWP_DEVIATION_TOLERANCE_THRESHOLD = 20;
const DWP_DEVIATION_HIDABLE_WARNING_THRESHOLD = 50;

function checkDwpDeviationWithinTolerance(dwpDeviation: number): boolean {
   return dwpDeviation <= DWP_DEVIATION_TOLERANCE_THRESHOLD;
}

function checkDwpDeviationWarningHidable(dwpDeviation: number): boolean {
   return dwpDeviation <= DWP_DEVIATION_HIDABLE_WARNING_THRESHOLD;
}

const numToString = (value: number | null | undefined, fallback = '0'): string => (value != null ? String(value) : fallback);

const isEmpty = (value: unknown): boolean => value === undefined || value === null || value === '';

export const pduPruToReadableName = (id: string): string => {
   const match = pduPruReadableNames.find((obj) => obj.key === id);
   return match?.value ?? id;
};

export const pduPruToId = (name: string): string => {
   const match = pduPruReadableNames.find((obj) => obj.value === name);
   return match?.key ?? name;
};

export const piaImportStatus = (updatedByPia: boolean, newFromPia: boolean): string => {
   if (newFromPia) return 'New';
   if (updatedByPia) return 'Updated';
   return '';
};

export const convertBackendSubItemToFrontendSubItem = (
   parentItemNo: string,
   backendSubItem: BackendSubItem,
   matSetName: string
): SubItem => {
   const updatedAtDate = new Date(backendSubItem.updatedAt.toString());
   const comparisonDate = new Date('0002-01-01');

   const subItem: SubItem = {
      parentId: parentItemNo,
      matSetName: matSetName,
      updatedAt: updatedAtDate < comparisonDate ? '' : formatDate(backendSubItem.updatedAt.toString()) || '',
      id: backendSubItem.id,
      part: backendSubItem.materialPart,
      partDetail: backendSubItem.materialType,
      totalWeight: backendSubItem.totalWeight != null ? Math.round(backendSubItem.totalWeight) : 0,
      materialGradeId: parseMaterialGradeId(backendSubItem.materialGradeId) ?? '0',
      materialGradeIdWarning: backendSubItem.materialGradeIdWarning,
      materialGrade: backendSubItem.materialPartText,
      density: numToString(backendSubItem.density),
      recycledContent: numToString(backendSubItem.recycledContent),
      renewableContent: numToString(backendSubItem.renewableContent),
      emissionFactorRenewable: backendSubItem.emissionFactorRenewable ?? 0,
      emissionFactorRecycled: backendSubItem.emissionFactorRecycled ?? 0,
      emissionFactorNonRenewable: backendSubItem.emissionFactorNonRenewable ?? 0,
      totalEmission: backendSubItem.totalEmission != null ? Math.round(backendSubItem.totalEmission) : 0,
      comment: backendSubItem.comment,
      mass: numToString(backendSubItem.mass),
      quantity: numToString(backendSubItem.quantity),
      threeDModelNo: backendSubItem.threeDModelNo,
      updatedBy: backendSubItem.updatedBy,
      volume: numToString(backendSubItem.volume),
      active: backendSubItem.active,
      clientCreated: backendSubItem.clientCreated,
      locked: backendSubItem.locked,
      dwpTotalWeight: Math.round(backendSubItem.averageWeight),
      dwpItemMatSet: { suppliers: backendSubItem.supplierWeights === null ? [] : [...backendSubItem.supplierWeights] },
      suppliers: [],
      compositionShare: backendSubItem.compositionShare,
      newFromPia: backendSubItem.newFromPia,
   };

   return omitBy(isEmpty)(subItem) as SubItem;
};

export const getSubItems = (backendItem: BackendItem): SubItem[] => {
   const subItems: SubItem[] = [];
   backendItem?.materialSets?.forEach((matSet) => {
      matSet?.supplierMaterials?.forEach((backendSubItem) => {
         const subItem = convertBackendSubItemToFrontendSubItem(backendItem.itemNo, backendSubItem, matSet.name);
         subItem.bomValidFrom = backendItem.bomValidFrom || null;
         subItems.push(subItem);
      });
   });
   return subItems;
};

export const convertBackendItemToFrontendItem = (backendItem: BackendItem): Item => {
   const subItems = getSubItems(backendItem);
   const { cpNetWeight, itemPartsWeight, dwpDeviation, itemPartsEmission } = calculateWeights(backendItem);
   const isDwpDeviationWithinTolerance = checkDwpDeviationWithinTolerance(dwpDeviation);
   const isDwpDeviationWarningHidable = checkDwpDeviationWarningHidable(dwpDeviation);
   const isBasedOnOtherItem = backendItem.parentItemNo !== null;
   const isLoose = !isBasedOnOtherItem && !backendItem.isBase;

   return {
      id: backendItem.itemNo,
      itemNo: backendItem.itemNo,
      buCodePru: pduPruToReadableName(backendItem.buCodePru),
      buCodePdu: pduPruToReadableName(backendItem.buCodePdu),
      cpNetWeight: cpNetWeight,
      partsWeight: itemPartsWeight,
      partsEmission: itemPartsEmission,
      dwpDeviation,
      pa: backendItem.pa,
      itemName: backendItem.itemName,
      familyName: backendItem.familyName,
      hfb: backendItem.hfb,
      itemState: backendItem.itemState,
      updatedByPia: backendItem.updatedByPia,
      newFromPia: backendItem.newFromPia,
      piaImportStatus: piaImportStatus(backendItem.updatedByPia, backendItem.newFromPia),
      saleStartDateGlobal: formatDate(backendItem.saleStartDateGlobal?.toString()) ?? '',
      saleEndDateGlobal: formatDate(backendItem.saleEndDateGlobal?.toString()) ?? '',
      subItems: subItems,
      progress: backendItem.progress,
      recycledContent: Math.round(backendItem.recycledContent),
      dwpActCpInfo: { suppliers: backendItem.supplierWeights === null ? [] : [...backendItem.supplierWeights] },
      deleted: backendItem.deleted,
      dwpWarningEnabled: backendItem.dwpWarningEnabled ?? true,
      isDwpDeviationWithinTolerance,
      isDwpDeviationWarningHidable,
      parentItemNo: backendItem.parentItemNo,
      isBasedOnOtherItem: isBasedOnOtherItem,
      isLoose,
      isBase: backendItem.isBase,
      bomValidFrom: backendItem.bomValidFrom || null,
   };
};
